import { Grid3x3, List, Plus, Users } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaSearchInput } from "@/shared/components/forms/ColocaSearchInput";
import { useAppShellBreadcrumb } from "@/shared/components/layout/useAppShellBreadcrumb";
import { useUrlParamMirror } from "@/shared/hooks/useUrlParamMirror";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import { CardsHeader } from "../components/CardsHeader";
import { CreateHolderModal } from "../components/CreateHolderModal";
import { HolderCard } from "../components/HolderCard";
import { HolderRow } from "../components/HolderRow";
import { IDENTITY_SEARCH_MIN_LENGTH } from "../constants/identities.constants";
import { useIdentitiesList, type IdentitiesListFilter } from "../hooks/useIdentitiesList";

type HoldersView = "list" | "grid";

const FILTER_OPTIONS: ReadonlyArray<{
  id: IdentitiesListFilter;
  labelKey: string;
}> = [
  { id: "all", labelKey: "identities_filter_all" },
  { id: "approved", labelKey: "identities_filter_approved" },
  { id: "pending", labelKey: "identities_filter_pending" },
  { id: "rejected", labelKey: "identities_filter_rejected" },
];

const FILTER_IDS = FILTER_OPTIONS.map((option) => option.id);

export const IdentitiesPage = () => {
  const { t } = useTranslations("cards");
  const { t: tGlobal } = useTranslations("global");
  const navigate = useNavigate();

  const breadcrumb = useMemo(
    () => [
      { label: tGlobal("sidebar_section_tarjetas") },
      { label: tGlobal("sidebar_menu_cards"), to: "/app/cards" },
      { label: t("cards_tab_label_identities"), to: "/app/cards/identities" },
    ],
    [t, tGlobal],
  );
  useAppShellBreadcrumb(breadcrumb);

  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setView] = useState<HoldersView>("list");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // Filters seed from the URL once, on mount, and every change is mirrored back
  // with `replace` — so a filtered view is linkable and shareable, but the back
  // button does NOT step through individual filter or search changes. That is
  // deliberate (typing would otherwise push a history entry per keystroke) and
  // it is what every search-param write in this module does; see `CardsPage`.
  // Note the seeding is read-once: there is no effect syncing the URL back into
  // state, so an external URL change would not move the filters either.
  const {
    identities,
    total,
    counts,
    hasMore,
    isLoading,
    isLoadingMore,
    error,
    query,
    setQuery,
    isSearchTermTooShort,
    hasActiveFilters,
    filter,
    setFilter,
    loadMore,
    reload,
  } = useIdentitiesList({
    loadErrorFallback: t("identities_error_load"),
    withAggregates: true,
    withCounts: true,
    initialQuery: searchParams.get("q") ?? "",
    initialFilter: (() => {
      const fromUrl = searchParams.get("filter");
      return fromUrl && (FILTER_IDS as readonly string[]).includes(fromUrl)
        ? (fromUrl as IdentitiesListFilter)
        : "all";
    })(),
  });

  // Same rule as the cards list: the search term reaches the URL through the mirror, never from `onChange`.
  // One `history.replaceState` per character trips WebKit's 100-per-10-seconds limit, and that throw lands
  // inside react-router's navigation — see `useUrlParamMirror`.
  useUrlParamMirror("q", query);

  const handleFilterChange = useCallback(
    (next: IdentitiesListFilter) => {
      setFilter(next);
      // Guarded like the cards list: the chips fire on every click, active or not, and react-router replaces
      // the history entry even when the URL is byte-identical.
      const nextParams = new URLSearchParams(searchParams);
      if (next === "all") nextParams.delete("filter");
      else nextParams.set("filter", next);
      if (nextParams.toString() === searchParams.toString()) return;

      setSearchParams(nextParams, { replace: true });
    },
    [searchParams, setFilter, setSearchParams],
  );

  const handleOpen = useCallback(
    (identityId: string) => navigate(`/app/cards/identities/${identityId}`),
    [navigate],
  );

  const handleCreated = useCallback(() => {
    reload();
    setSearchParams(
      (prev) => {
        prev.delete("q");
        prev.delete("filter");
        return prev;
      },
      { replace: true },
    );
  }, [reload, setSearchParams]);

  return (
    <div className="mx-auto w-full max-w-[1400px] space-y-6 p-2">
      <CardsHeader
        activeTabId="identities"
        actions={
          <ColocaButton variant="primary" icon={Plus} onClick={() => setIsCreateModalOpen(true)}>
            {t("identities_header_new_holder")}
          </ColocaButton>
        }
      />

      <section className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="rounded-2xl border border-border bg-card p-4">
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
            {t("identities_stats_total")}
          </div>
          <div className="mt-1 text-2xl font-semibold text-foreground">{counts.all}</div>
        </div>
        <div className="rounded-2xl border border-border bg-card p-4">
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
            {t("identities_stats_kyc_approved")}
          </div>
          <div className="mt-1 text-2xl font-semibold text-success">{counts.approved}</div>
        </div>
        <div className="rounded-2xl border border-border bg-card p-4">
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
            {t("identities_stats_kyc_pending")}
          </div>
          <div className="mt-1 text-2xl font-semibold text-warning">{counts.pending}</div>
        </div>
      </section>

      <section className="space-y-3">
        <div className="flex flex-wrap items-end justify-between gap-2">
          <div>
            <h2 className="text-base font-semibold leading-tight text-foreground">
              {t("identities_list_section_title")}
            </h2>
            <p className="mt-0.5 font-mono text-xs text-muted-foreground">
              {identities.length} {t("cards_list_of_total")} {total} {t("cards_list_results_label")}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-1.5">
            {FILTER_OPTIONS.map((option) => {
              const active = option.id === filter;
              const count = option.id === "all" ? counts.all : counts[option.id];
              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => handleFilterChange(option.id)}
                  className={cn(
                    "inline-flex h-8 items-center gap-1.5 rounded-[var(--radius-md)] border px-2.5 text-[13px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
                    active
                      ? "border-foreground bg-foreground text-background"
                      : "border-border bg-background text-foreground hover:bg-muted",
                  )}
                >
                  {t(option.labelKey as never)}
                  <span
                    className={cn(
                      "font-mono text-[10px]",
                      active ? "text-background/70" : "text-muted-foreground",
                    )}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="flex items-center gap-2">
            <div className="flex flex-col gap-1">
              <ColocaSearchInput
                value={query}
                onValueChange={setQuery}
                placeholder={t("identities_search_placeholder")}
                className="sm:w-[260px]"
              />
              {isSearchTermTooShort && (
                <span className="text-[11px] text-muted-foreground">
                  {t("identities_search_min_length_hint").replace(
                    "{count}",
                    String(IDENTITY_SEARCH_MIN_LENGTH),
                  )}
                </span>
              )}
            </div>

            <div className="inline-flex rounded-[var(--radius-md)] border border-border bg-background p-0.5">
              <button
                type="button"
                onClick={() => setView("list")}
                aria-label={t("identities_view_list")}
                aria-pressed={view === "list"}
                className={cn(
                  "inline-flex h-7 w-7 items-center justify-center rounded-[6px] transition-colors",
                  view === "list" ? "bg-muted text-foreground" : "text-muted-foreground",
                )}
              >
                <List className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => setView("grid")}
                aria-label={t("identities_view_grid")}
                aria-pressed={view === "grid"}
                className={cn(
                  "inline-flex h-7 w-7 items-center justify-center rounded-[6px] transition-colors",
                  view === "grid" ? "bg-muted text-foreground" : "text-muted-foreground",
                )}
              >
                <Grid3x3 className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </div>

        {error ? (
          <div className="rounded-2xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
            {error}
          </div>
        ) : isLoading && identities.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <div className="flex flex-col items-center gap-3">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
              <span className="text-sm text-muted-foreground">{t("identities_loading")}</span>
            </div>
          </div>
        ) : identities.length === 0 ? (
          /* "No results for these filters" is a different statement from "this
             organization has no holders" — the old screen said the second one
             either way, which read as data loss during a search. */
          <ColocaEmpty
            icon={Users}
            title={hasActiveFilters ? t("identities_empty_filtered_title") : t("identities_empty_title")}
            description={
              hasActiveFilters
                ? t("identities_empty_filtered_description")
                : t("identities_empty_description")
            }
          />
        ) : (
          <>
            {view === "list" ? (
              <div className="space-y-2">
                {identities.map((identity) => (
                  <HolderRow key={identity.id} identity={identity} onOpen={handleOpen} />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {identities.map((identity) => (
                  <HolderCard key={identity.id} identity={identity} onOpen={handleOpen} />
                ))}
              </div>
            )}

            {/* No longer gated on the search box: the filters are applied by the
                backend, so paging continues within the filtered result set. */}
            {hasMore && (
              <div className="flex justify-center pt-2">
                <ColocaButton variant="default" onClick={loadMore} loading={isLoadingMore}>
                  {isLoadingMore ? t("cards_loading_more") : t("cards_load_more")}
                </ColocaButton>
              </div>
            )}
          </>
        )}
      </section>

      <CreateHolderModal
        open={isCreateModalOpen}
        onOpenChange={setIsCreateModalOpen}
        onCreated={handleCreated}
      />
    </div>
  );
};
